import {createContext, type PropsWithChildren, use, useMemo} from 'react'
import type {BrandedDividerParams, DitherParams} from '@github-ui/brand-webgl'

import type {Brand} from '../../types'

type WebGLDitherSettings = {
  params: DitherParams
}

type WebGLBrandedDividerSettings = {
  params: BrandedDividerParams
}

export type BrandTreatmentSettings = {
  brand?: Brand
  dither: WebGLDitherSettings
  brandedDivider: WebGLBrandedDividerSettings | null
}

export type BrandTreatmentProviderProps = PropsWithChildren<{
  brand?: Brand
}>

const DEFAULT_DITHER_PARAMS: DitherParams = {
  color: 'pearl',
  type: 'wave',
  fadeIn: true,
}

const DITHER_PARAMS: Record<Brand, DitherParams> = {
  Mona: DEFAULT_DITHER_PARAMS,
  Copilot: {color: 'copilot', type: 'wave', fadeIn: true},
  Security: {color: 'security', type: 'wave', fadeIn: true},
  General: {color: 'default', type: 'wave', fadeIn: true},
}

const BRANDED_DIVIDER_PARAMS: Record<Brand, BrandedDividerParams> = {
  Mona: {character: 'mona', layout: 0},
  Copilot: {character: 'copilot', layout: 0},
  Security: {character: 'security', layout: 0},
  General: {character: 'invertocat', layout: 0},
}

const BrandTreatmentContext = createContext<BrandTreatmentSettings | undefined>(undefined)
export type BrandTreatmentContextType = React.ContextType<typeof BrandTreatmentContext>

export function BrandTreatmentProvider({brand, children}: BrandTreatmentProviderProps) {
  const value = useMemo<BrandTreatmentSettings>(() => {
    if (!brand) {
      return {
        dither: {params: DEFAULT_DITHER_PARAMS},
        brandedDivider: null,
      }
    }

    const ditherParams = DITHER_PARAMS[brand]
    const brandedDividerParams = BRANDED_DIVIDER_PARAMS[brand]

    return {
      brand,
      dither: {params: ditherParams},
      brandedDivider: {params: brandedDividerParams},
    }
  }, [brand])

  return <BrandTreatmentContext value={value}>{children}</BrandTreatmentContext>
}

export function useBrandTreatment(): BrandTreatmentSettings {
  const context = use(BrandTreatmentContext)

  if (!context) {
    throw new Error('useBrandTreatment must be used within a BrandTreatmentProvider.')
  }

  return context
}
